import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Download, Phone, Users, UserPlus, Filter, Trash2, Globe } from "lucide-react";
import { formatSydney, formatTableDateSydney, getCurrentSydneyDisplay } from "../../utils/timezone";

export default function AdminPerformance() {
  const { api } = useAuth();
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [serverTime, setServerTime] = useState(null);
  
  const [filters, setFilters] = useState({
    user_id: "all",
    start_date: new Date(new Date().setDate(new Date().getDate() - 30)),
    end_date: new Date(),
  });

  const fetchData = async () => {
    try {
      const [activitiesRes, usersRes] = await Promise.all([
        api().get("/admin/activities", {
          params: {
            user_id: filters.user_id === "all" ? undefined : filters.user_id,
            start_date: format(filters.start_date, "yyyy-MM-dd"),
            end_date: format(filters.end_date, "yyyy-MM-dd"),
          },
        }),
        api().get("/admin/users"),
      ]);
      setActivities(activitiesRes.data);
      setUsers(usersRes.data.filter(u => u.role === "staff"));
    } catch (error) {
      toast.error("Failed to load performance data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api().get("/admin/export/performance", {
        params: {
          start_date: format(filters.start_date, "yyyy-MM-dd"),
          end_date: format(filters.end_date, "yyyy-MM-dd"),
        },
        responseType: "blob",
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `performance_${format(filters.start_date, "yyyy-MM-dd")}_${format(filters.end_date, "yyyy-MM-dd")}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Report exported!");
    } catch (error) {
      toast.error("Failed to export report");
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteActivity = async (activityId) => {
    try {
      await api().delete(`/admin/activities/${activityId}`);
      toast.success("Activity deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete activity");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  // Calculate totals
  const totals = activities.reduce(
    (acc, a) => ({
      calls: acc.calls + (a.calls_made || 0),
      potential: acc.potential + (a.potential_leads || 0),
      signups: acc.signups + (a.signups || 0),
    }),
    { calls: 0, potential: 0, signups: 0 }
  );

  return (
    <div className="space-y-8" data-testid="admin-performance">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Performance Reports</h1>
          <p className="text-slate-500 mt-1">Analyze staff activity and performance</p>
        </div>
        
        <Button
          onClick={handleExport}
          disabled={exporting}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="export-performance-btn"
        >
          <Download className="mr-2 h-4 w-4" />
          {exporting ? "Exporting..." : "Export CSV"}
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Staff Member</Label>
              <Select
                value={filters.user_id}
                onValueChange={(value) => setFilters({ ...filters, user_id: value })}
              >
                <SelectTrigger data-testid="filter-staff-select">
                  <SelectValue placeholder="All Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="filter-start-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(filters.start_date, "MMM dd, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.start_date}
                    onSelect={(date) => date && setFilters({ ...filters, start_date: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="filter-end-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(filters.end_date, "MMM dd, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={filters.end_date}
                    onSelect={(date) => date && setFilters({ ...filters, end_date: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Phone className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">{totals.calls}</p>
                <p className="text-sm text-slate-500">Total Calls</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-50 rounded-lg">
                <Users className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">{totals.potential}</p>
                <p className="text-sm text-slate-500">Potential Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-50 rounded-lg">
                <UserPlus className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-mono">{totals.signups}</p>
                <p className="text-sm text-slate-500">Total Signups</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activity Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg">Activity Details</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Staff</th>
                  <th>Calls</th>
                  <th>Potential</th>
                  <th>Signups</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {activities.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-slate-500">
                      No activity data found for the selected period
                    </td>
                  </tr>
                ) : (
                  activities.map((activity) => (
                    <tr key={activity.id} data-testid={`activity-row-${activity.id}`}>
                      <td className="font-medium">
                        {formatTableDateSydney(activity.date)}
                      </td>
                      <td>{activity.user_name}</td>
                      <td className="font-mono">{activity.calls_made}</td>
                      <td className="font-mono">{activity.potential_leads}</td>
                      <td className="font-mono">{activity.signups}</td>
                      <td className="text-slate-500 text-sm max-w-[200px] truncate">
                        {activity.notes || "-"}
                      </td>
                      <td>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-600 hover:bg-red-50"
                              data-testid={`delete-activity-${activity.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Activity?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete the activity for {activity.user_name} on {formatTableDateSydney(activity.date)}. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteActivity(activity.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
